import { useState } from 'react'
import { supabase } from '../api/supabase'

export interface JobDocument {
  id: string
  job_id: string
  doc_type: 'MBL' | 'HBL' | 'INVOICE' | 'OTHER'
  file_name: string
  file_url: string
  file_size?: number
  mime_type?: string
  ai_parsed: boolean
  ai_result?: Record<string, any>
  ai_confidence?: number
  uploaded_by?: string
  uploaded_at: string
}

export interface ParsedDocumentData {
  mbl_number?: string
  hbl_number?: string
  shipper?: string
  consignee?: string
  notify_party?: string
  port_of_loading?: string
  port_of_discharge?: string
  vessel_name?: string
  voyage_number?: string
  container_numbers?: string[]
  goods_description?: string
  total_weight?: number
  total_volume?: number
  [key: string]: any
}

export function useJobDocuments() {
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Fetch all documents for a job
   */
  const fetchJobDocuments = async (jobId: string): Promise<JobDocument[]> => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('job_documents')
        .select('*')
        .eq('job_id', jobId)
        .order('uploaded_at', { ascending: false })

      if (fetchError) throw fetchError

      return data || []
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch documents'
      setError(errorMsg)
      console.error('Error fetching job documents:', err)
      return []
    } finally {
      setLoading(false)
    }
  }

  /**
   * Upload a document for a job
   * @param jobId The job ID
   * @param file The file to upload
   * @param docType The document type (MBL, HBL, INVOICE, OTHER)
   * @param uploadedBy User email or identifier
   */
  const uploadDocument = async (
    jobId: string,
    file: File,
    docType: 'MBL' | 'HBL' | 'INVOICE' | 'OTHER',
    uploadedBy?: string
  ): Promise<JobDocument | null> => {
    try {
      setUploading(true)
      setError(null)

      // Validate file
      if (!file) {
        throw new Error('No file selected')
      }

      // Check file size (max 50MB)
      const maxSize = 50 * 1024 * 1024
      if (file.size > maxSize) {
        throw new Error('File size exceeds 50MB limit')
      }

      // For PDFs, validate MIME type
      if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
        throw new Error('Only PDF files are supported')
      }

      // Upload file to Supabase Storage
      const fileName = `${jobId}/${docType}/${Date.now()}_${file.name}`
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('job-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('job-documents')
        .getPublicUrl(uploadData.path)

      // Create document record in database
      const { data: docData, error: docError } = await supabase
        .from('job_documents')
        .insert({
          job_id: jobId,
          doc_type: docType,
          file_name: file.name,
          file_url: urlData.publicUrl,
          file_size: file.size,
          mime_type: file.type,
          ai_parsed: false,
          uploaded_by: uploadedBy
        })
        .select()
        .single()

      if (docError) throw docError

      return docData as JobDocument
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to upload document'
      setError(errorMsg)
      console.error('Error uploading document:', err)
      return null
    } finally {
      setUploading(false)
    }
  }

  /**
   * Delete a document
   * @param documentId The document ID
   * @param filePath The storage file path
   */
  const deleteDocument = async (documentId: string, filePath: string): Promise<boolean> => {
    try {
      setLoading(true)
      setError(null)

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('job-documents')
        .remove([filePath])

      if (storageError) {
        console.warn('Warning: Failed to delete file from storage:', storageError)
        // Continue with database deletion even if storage delete fails
      }

      // Delete database record
      const { error: dbError } = await supabase
        .from('job_documents')
        .delete()
        .eq('id', documentId)

      if (dbError) throw dbError

      return true
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete document'
      setError(errorMsg)
      console.error('Error deleting document:', err)
      return false
    } finally {
      setLoading(false)
    }
  }

  /**
   * Parse a document with AI (Claude API)
   * Calls Supabase Edge Function to keep the API key secure on the backend
   */
  const parseDocumentWithAI = async (
    documentId: string,
    fileUrl: string
  ): Promise<{ data: ParsedDocumentData; confidence: number } | null> => {
    try {
      setParsing(true)
      setError(null)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      if (!supabaseUrl) {
        throw new Error('Missing VITE_SUPABASE_URL configuration')
      }

      // TODO: Implement Edge Function call to parse document
      // The flow will be:
      // 1. Download PDF from fileUrl
      // 2. Extract text and send to Claude API
      // 3. Parse response and return results
      // 4. Save results to job_documents table

      console.log('🔄 Preparing PDF parsing for:', { documentId, fileUrl })

      // Placeholder until Edge Function is implemented
      const mockData: ParsedDocumentData = {
        mbl_number: 'MOCK-MBL-12345',
        hbl_number: 'MOCK-HBL-67890',
        shipper: 'To be implemented',
        consignee: 'To be implemented',
        port_of_loading: 'To be implemented',
        port_of_discharge: 'To be implemented'
      }

      // Update document with parsed data
      const { error: updateError } = await supabase
        .from('job_documents')
        .update({
          ai_parsed: true,
          ai_result: mockData,
          ai_confidence: 0.85
        })
        .eq('id', documentId)

      if (updateError) throw updateError

      return { data: mockData, confidence: 0.85 }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to parse document'
      setError(errorMsg)
      console.error('Error parsing document:', err)
      return null
    } finally {
      setParsing(false)
    }
  }

  return {
    loading,
    uploading,
    parsing,
    error,
    fetchJobDocuments,
    uploadDocument,
    deleteDocument,
    parseDocumentWithAI
  }
}
